import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyMenuItemsDataQuery } from '../../hooks/queries/useDashboardQuery';
import './MonthlyItemSalesChart.css';

interface MonthlyData {
  net_sales: string;
  net_sales_raw: number; 
  quantity: number;
  percentage: string;
  percentage_raw: number; 
  growth_rate: string | null;
  total_monthly_sales: string;
}

interface MenuItem {
  menu_item: string;
  monthly_data: { [month: string]: MonthlyData };
  avg_percentage: number;
}

interface Category {
  menu_group: string;
  menu_category: string;
  sorted_items: MenuItem[];
}

interface MonthlyMenuData {
  months: string[];
  categories: { [key: string]: Category };
}

const getSalesforceIdFromUrl = (): string => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('storeId') || 'a0JPC00000cKHlB2AW';
};

const formatMonth = (monthStr: string): string => {
  try {
    const date = new Date(monthStr + 'T00:00:00.000Z'); 
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    return `${String(month).padStart(2, '0')}/${year}`;
  } catch (error) {
    console.error('Date parsing error:', error, monthStr);
    return monthStr; 
  }
};


const formatCurrency = (value: number): string => {
  if (isNaN(value) || value === null || value === undefined) {
    return '$0.00';
  }
  
  return value.toLocaleString('en-US', { 
    style: 'currency', 
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const getGrowthDisplay = (growthRate: string | null) => {
  if (!growthRate || growthRate === '0') {
    return { color: '#666', icon: '', display: '-' };
  }
  
  const rate = parseFloat(growthRate);
  if (rate > 0) {
    return { 
      color: '#28a745', 
      icon: '▲', 
      display: `+${Math.abs(rate).toFixed(1)}%` 
    };
  } else {
    return { 
      color: '#dc3545', 
      icon: '▼', 
      display: `-${Math.abs(rate).toFixed(1)}%` 
    };
  }
};


const processV2ApiData = (apiData: any[]): MonthlyMenuData => {
  const monthsSet = new Set<string>();
  const categoriesMap: { [key: string]: { [item: string]: { [month: string]: any } } } = {};
  const monthlyTotals: { [month: string]: number } = {};
  
  
  apiData.forEach(row => {
    const { year_month, menu_group, menu_category, menu_item, net_sales, quantity } = row;
    
    monthsSet.add(year_month);
    
    const categoryKey = `${menu_group}|${menu_category}`;
    if (!categoriesMap[categoryKey]) {
      categoriesMap[categoryKey] = {};
    }
    if (!categoriesMap[categoryKey][menu_item]) {
      categoriesMap[categoryKey][menu_item] = {};
    }
    
    
    const salesValue = typeof net_sales === 'string' ? parseFloat(net_sales) : parseFloat(net_sales);
    const validSales = isNaN(salesValue) ? 0 : salesValue;
    
    categoriesMap[categoryKey][menu_item][year_month] = {
      net_sales: validSales,
      quantity: parseInt(quantity) || 0
    };
    
    
    if (!monthlyTotals[year_month]) {
      monthlyTotals[year_month] = 0;
    }
    monthlyTotals[year_month] += validSales;
  });

  
  const sortedMonths = Array.from(monthsSet).sort();
  
  
  const categories: { [key: string]: Category } = {};
  
  Object.keys(categoriesMap).forEach(categoryKey => {
    const [menu_group, menu_category] = categoryKey.split('|');
    const items = categoriesMap[categoryKey];
    
    const sortedItems: MenuItem[] = Object.keys(items).map(itemName => {
      const monthlyData: { [month: string]: MonthlyData } = {};
      let totalSales = 0;
      let totalPercentage = 0;
      let monthsWithData = 0;
      
      
      sortedMonths.forEach((month, index) => {
        const currentMonthData = items[itemName][month];
        const currentSales = currentMonthData ? (currentMonthData.net_sales || 0) : 0;
        const hasData = currentMonthData && currentSales > 0;
        
        
        if (hasData) {
          
          const percentage = monthlyTotals[month] > 0 ? (currentSales / monthlyTotals[month]) * 100 : 0;
          
          
          let growthRate: string | null = null;
          if (index > 0) {
            const prevMonth = sortedMonths[index - 1];
            const prevMonthData = items[itemName][prevMonth];
            const prevSales = prevMonthData ? (prevMonthData.net_sales || 0) : 0;
            
            if (prevSales > 0 && currentSales >= 0) {
              const growth = ((currentSales - prevSales) / prevSales) * 100;
              growthRate = isNaN(growth) ? null : growth.toFixed(1);
            } else if (currentSales > 0 && prevSales === 0) {
              growthRate = '100.0'; 
            }
          }
          

          monthlyData[month] = {
            net_sales: formatCurrency(currentSales),
            net_sales_raw: currentSales,
            quantity: currentMonthData.quantity || 0,
            percentage: (percentage || 0).toFixed(1) + '%',
            percentage_raw: percentage || 0,
            growth_rate: growthRate,
            total_monthly_sales: formatCurrency(monthlyTotals[month])
          };
          
          totalSales += currentSales;
          totalPercentage += percentage;
          monthsWithData++;
        } else {
          
          monthlyData[month] = {
            net_sales: '-',
            net_sales_raw: 0,
            quantity: 0,
            percentage: '-',
            percentage_raw: 0,
            growth_rate: null,
            total_monthly_sales: monthlyTotals[month] > 0 ? formatCurrency(monthlyTotals[month]) : '-'
          };
        }
      });
      
      
      const avgPercentage = monthsWithData > 0 ? totalPercentage / monthsWithData : 0;
      
      return {
        menu_item: itemName,
        monthly_data: monthlyData,
        avg_percentage: parseFloat(avgPercentage.toFixed(1))
      };
    });
    
    
    sortedItems.sort((a, b) => {
      const aTotalSales = Object.values(a.monthly_data).reduce((sum, data) => {
        return sum + (data.net_sales_raw || 0);
      }, 0);
      const bTotalSales = Object.values(b.monthly_data).reduce((sum, data) => {
        return sum + (data.net_sales_raw || 0);
      }, 0);
      return bTotalSales - aTotalSales;
    });
    
    categories[categoryKey] = {
      menu_group,
      menu_category,
      sorted_items: sortedItems
    };
  });

  return {
    months: sortedMonths,
    categories
  };
};

const MonthlyItemSalesChart: React.FC = () => {
  const { t } = useTranslation();
  const salesforceId = getSalesforceIdFromUrl();
  
  
  const isLaptop = window.innerWidth <= 1600;
  const isIPad = window.innerWidth <= 1024;
  const isIPhone = window.innerWidth <= 480;
  
  
  const { 
    data: rawData, 
    isLoading: loading, 
    error 
  } = useMonthlyMenuItemsDataQuery(salesforceId);
  
  
  const data = useMemo(() => {
    if (!rawData || !Array.isArray(rawData)) {
      return null;
    }

    try {
      
      return processV2ApiData(rawData);
    } catch (err) {
      console.error('Data processing error:', err);
      return null;
    }
  }, [rawData]);

  const handleRowMouseEnter = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const row = event.currentTarget;
    const categoryKey = row.getAttribute('data-category');
    
    if (categoryKey) {
      const categoryRows = Array.from(document.querySelectorAll(`[data-category="${categoryKey}"]`));
      
      let categoryRow: Element | null = null;
      for (const r of categoryRows) {
        const categoryCell = r.querySelector('.category-cell');
        if (categoryCell) {
          categoryRow = r;
          break;
        }
      }
      
      if (categoryRow) {
        const categoryCell = categoryRow.querySelector('.category-cell');
        if (categoryCell) {
          categoryCell.classList.add('category-highlight');
        }
      }
      
      row.classList.add('row-highlight');
    }
  };

  const handleRowMouseLeave = (event: React.MouseEvent<HTMLTableRowElement>) => {
    const row = event.currentTarget;
    const categoryKey = row.getAttribute('data-category');
    
    if (categoryKey) {
      const categoryRows = Array.from(document.querySelectorAll(`[data-category="${categoryKey}"]`));
      
      let categoryRow: Element | null = null;
      for (const r of categoryRows) {
        const categoryCell = r.querySelector('.category-cell');
        if (categoryCell) {
          categoryRow = r;
          break;
        }
      }
      
      if (categoryRow) {
        const categoryCell = categoryRow.querySelector('.category-cell');
        if (categoryCell) {
          categoryCell.classList.remove('category-highlight');
        }
      }
      
      row.classList.remove('row-highlight');
    }
  };

  if (loading) {
    return (
      <div className="monthly-item-sales-chart">
        <div className="monthly-item-sales-chart-container">
          <div className="monthly-item-sales-loading">
            <div className="monthly-item-sales-loading-spinner"></div>
            <div className="monthly-item-sales-loading-text" style={{ 
              fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('monthlyItemsTable.loading')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : t('monthlyItemsTable.loadingFailed', { message: 'Unknown error' });
    return (
      <div className="monthly-item-sales-chart">
        <div className="monthly-item-sales-chart-container">
          <div className="monthly-item-sales-error">
            <div className="monthly-item-sales-error-icon">⚠️</div>
            <div className="monthly-item-sales-error-text" style={{ 
              fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {errorMessage}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.months || data.months.length === 0) {
    return (
      <div className="monthly-item-sales-chart">
        <div className="monthly-item-sales-chart-container">
          <div className="monthly-item-sales-empty">
            <div className="monthly-item-sales-empty-icon">📊</div>
            <div className="monthly-item-sales-empty-text" style={{ 
              fontSize: isIPhone ? '12px' : (isIPad ? '13px' : (isLaptop ? '14px' : '16px'))
            }}>
              {t('monthlyItemsTable.emptyData')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="monthly-item-sales-chart">
      <div className="monthly-item-sales-chart-container">
        <div className="monthly-table-wrapper">
          <table className="monthly-sales-table">
            <thead>
              <tr>
                <th className="category-header" style={{
                  fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '12px' : '13px')),
                  padding: isIPhone ? '6px 4px' : (isIPad ? '8px 6px' : (isLaptop ? '10px 8px' : '12px 10px'))
                }}>
                  {t('monthlyItemsTable.categoryHeader')}
                </th>
                <th className="item-header" style={{
                  fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '12px' : '13px')),
                  padding: isIPhone ? '6px 4px' : (isIPad ? '8px 6px' : (isLaptop ? '10px 8px' : '12px 10px'))
                }}>
                  {t('monthlyItemsTable.itemHeader')}
                </th>
                {data.months.map((month, index) => (
                  <th key={`month-header-${index}-${month}`} className="month-header" style={{
                    fontSize: isIPhone ? '9px' : (isIPad ? '10px' : (isLaptop ? '11px' : '12px')),
                    padding: isIPhone ? '6px 3px' : (isIPad ? '8px 4px' : (isLaptop ? '10px 6px' : '12px 8px'))
                  }}>
                    {formatMonth(month)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.categories).map(([categoryKey, category]) => {
                const categoryRowSpan = category.sorted_items.length;
                
                return category.sorted_items.map((item, itemIndex) => {
                  const growth = item.monthly_data[data.months[data.months.length - 1]]?.growth_rate;
                  const growthDisplay = getGrowthDisplay(growth);
                  
                  return (
                    <tr 
                      key={`${categoryKey}-${item.menu_item}`} 
                      className="data-row"
                      data-category={categoryKey}
                      onMouseEnter={handleRowMouseEnter}
                      onMouseLeave={handleRowMouseLeave}
                    >
                      {itemIndex === 0 && (
                        <td className="category-cell" rowSpan={categoryRowSpan}>
                          <div className="category-info">
                            <div className="category-name" style={{
                              fontSize: isIPhone ? '10px' : (isIPad ? '11px' : (isLaptop ? '12px' : '13px')),
                              fontWeight: 'bold'
                            }}>
                              {category.menu_category}
                            </div>
                            <div className="category-group" style={{
                              fontSize: isIPhone ? '8px' : (isIPad ? '9px' : (isLaptop ? '10px' : '11px')),
                              color: '#666'
                            }}>
                              {category.menu_group}
                            </div>
                          </div>
                        </td>
                      )}
                      <td className="item-cell" style={{
                        padding: isIPhone ? '6px 4px' : (isIPad ? '8px 6px' : (isLaptop ? '10px 8px' : '12px 10px'))
                      }}>
                        <div className="item-info">
                          <span className="item-name" style={{
                            fontSize: isIPhone ? '9px' : (isIPad ? '10px' : (isLaptop ? '11px' : '12px')),
                            fontWeight: 'bold'
                          }}>
                            {item.menu_item}
                          </span>
                          <span className="item-avg-percentage" style={{
                            fontSize: isIPhone ? '8px' : (isIPad ? '9px' : (isLaptop ? '10px' : '11px')),
                            color: '#666'
                          }}>
                            {t('monthlyItemsTable.averagePercentage', { percentage: item.avg_percentage.toFixed(2) })}
                          </span>
                        </div>
                      </td>
                      {data.months.map((month, monthIndex) => {
                        const monthData = item.monthly_data[month];
                        if (!monthData) {
                          return (
                            <td key={`${categoryKey}-${item.menu_item}-${monthIndex}-${month}`} className="month-cell no-data" style={{
                              padding: isIPhone ? '4px 2px' : (isIPad ? '6px 3px' : (isLaptop ? '8px 4px' : '10px 6px'))
                            }}>
                              <div className="cell-content">
                                <div className="sales-amount-with-percentage" style={{
                                  fontSize: isIPhone ? '8px' : (isIPad ? '9px' : (isLaptop ? '10px' : '11px'))
                                }}>
                                  - (-)
                                </div>
                              </div>
                            </td>
                          );
                        }
                        
                        const growthDisplay = getGrowthDisplay(monthData.growth_rate);
                        
                        return (
                          <td key={`${categoryKey}-${item.menu_item}-${monthIndex}-${month}`} className="month-cell" style={{
                            padding: isIPhone ? '4px 2px' : (isIPad ? '6px 3px' : (isLaptop ? '8px 4px' : '10px 6px'))
                          }}>
                            <div className="cell-content">
                              <div className="sales-amount-with-percentage" style={{
                                fontSize: isIPhone ? '8px' : (isIPad ? '9px' : (isLaptop ? '10px' : '11px')),
                                fontWeight: 'bold'
                              }}>
                                {monthData.net_sales === '-' ? '-' : (
                                  `$${monthData.net_sales_raw.toLocaleString('en-US', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                  })} (${monthData.percentage_raw.toFixed(2)}%)`
                                )}
                              </div>
                              {monthData.growth_rate && (
                                <div 
                                  className="growth-rate"
                                  style={{ 
                                    color: growthDisplay.color,
                                    fontSize: isIPhone ? '7px' : (isIPad ? '8px' : (isLaptop ? '9px' : '10px'))
                                  }}
                                >
                                  {growthDisplay.icon} {growthDisplay.display}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonthlyItemSalesChart;